<template>
  <div class="map-page-container">
    <!-- ✅ MOBILE LAYOUT: Placeholder is first, map is second -->
    <!-- This placeholder shows only on mobile before the map is activated -->
    <div v-if="isMobile && !mapActiveOnMobile" @click="activateMap" class="map-placeholder">
      <ResponsiveImage :image="headerImage" alt="Map of Tulum Clubs"></ResponsiveImage>
      <div class="placeholder-overlay">
        <span v-if="!isLoadingMap" class="text-2xl font-bold">Tap to discover Tulum's nightlife! ✨</span>
        <div v-else class="loading-spinner-overlay">
          <div class="spinner"></div>
          <p class="mt-4 text-xl">Loading map...</p>
        </div>
      </div>
    </div>

    <!-- The actual map container -->
    <div
      v-show="!isMobile || mapActiveOnMobile"
      id="google-map-container"
      ref="mapContainer"
      :class="{ 'map-loading': isLoadingMap }"
    >
      <div v-if="isLoadingMap && mapActiveOnMobile" class="loading-spinner-overlay">
        <div class="spinner"></div>
        <p class="mt-4 text-xl">Loading map...</p>
      </div>
    </div>

    <div class="map-controls">
      <!-- Header image for desktop, or if map is active on mobile -->
      <ResponsiveImage :image="headerImage" v-show="!isMobile || mapActiveOnMobile"></ResponsiveImage>

      <header class="p-5">
        <h1 class="tulum-party-title text-3xl font-bold text-white mb-2">Tulum Nightlife Map</h1>
        <hr class="border-t border-gray-700 my-4">
        <div class="card bg-gray-800 p-4 rounded-lg shadow-md">
          <p class="text-gray-300 text-base">Visually explore every club in Tulum. Use the filters to find your perfect spot.</p>
        </div>
      </header>

      <!-- Active Filters Summary -->
      <div v-if="activeFiltersSummary" class="active-filters-summary px-5 py-3 bg-gray-700 rounded-lg mx-5 my-4 text-gray-200 text-sm shadow-inner">
        <p><strong>Active Filters:</strong> {{ activeFiltersSummary }}</p>
      </div>

      <!-- Search & Main Filters -->
      <div class="control-section px-5 mb-4">
        <input
          class="w-full p-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border border-gray-600 focus:outline-none focus:border-green-500"
          v-model="searchQuery"
          @input="activateMap"
          type="text"
          placeholder="Search name, vibe, music, DJs..."
        />
        <div class="mt-3 flex items-center">
          <input type="checkbox" id="open-now-checkbox" v-model="filterOpenNow" @change="activateMap" class="form-checkbox h-5 w-5 text-green-500 rounded-md border-gray-600 focus:ring-green-500" />
          <label for="open-now-checkbox" class="ml-2 text-gray-300 text-base">Open Now</label>
        </div>
      </div>

      <!-- Multi-Select Filters -->
      <Collapse class="mx-5">
        <CollapseItem title="Music Type">
          <div class="control-section">
            <div class="filter-group grid grid-cols-2 gap-2">
              <div v-for="music in filterOptions.music" :key="music" class="filter-checkbox flex items-center">
                <input type="checkbox" :id="`music-${music}`" :value="music" v-model="selectedMusic" @change="activateMap" class="form-checkbox h-4 w-4 text-green-500 rounded-sm border-gray-600 focus:ring-green-500" />
                <label :for="`music-${music}`" class="ml-2 text-gray-300 text-sm">{{ music }}</label>
              </div>
            </div>
          </div>
        </CollapseItem>

        <CollapseItem title="Good For">
          <div class="control-section">
            <div class="filter-group grid grid-cols-2 gap-2">
              <div v-for="goodFor in filterOptions.goodFor" :key="goodFor" class="filter-checkbox flex items-center">
                <input type="checkbox" :id="`goodFor-${goodFor}`" :value="goodFor" v-model="selectedGoodFor" @change="activateMap" class="form-checkbox h-4 w-4 text-green-500 rounded-sm border-gray-600 focus:ring-green-500" />
                <label :for="`goodFor-${goodFor}`" class="ml-2 text-gray-300 text-sm">{{ goodFor }}</label>
              </div>
            </div>
          </div>
        </CollapseItem>
      </Collapse>

      <div class="control-section px-5 mt-6 mb-8">
        <button @click="clearFilters" class="clear-filters-btn w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition duration-200 ease-in-out shadow-lg">
          Clear All Filters
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CLUBS_CONFIG from '../../config/tulum-clubs-config.json';
import Collapse from '@/components/Collapse/Collapse';
import CollapseItem from '@/components/Collapse/CollapseItem';
import ResponsiveImage from '@/components/HeadImage/ResponsiveImage';

export default {
  name: 'TulumClubsMap',
  components: { ResponsiveImage, CollapseItem, Collapse },
  metaInfo() {
    const pageUrl = 'https://www.tulum.party/tulum-clubs-map';
    return {
      title: 'Interactive Map of Tulum Clubs & Nightlife',
      link: [{ rel: 'canonical', href: pageUrl }],
      meta: [
        { hid: 'description', name: 'description', content: 'Explore all Tulum clubs, beach parties, and nightlife venues on an interactive map. Filter by open now, area, price, music and more to plan your perfect night out.' },
        { hid: 'og:url', property: 'og:url', content: pageUrl },
        { hid: 'og:title', property: 'og:title', content: 'Interactive Map of Tulum Clubs & Nightlife' },
      ]
    };
  },
  data() {
    return {
      clubsConfig: CLUBS_CONFIG,
      headerImage: 'https://imgix.cosmicjs.com/873febb0-f401-11eb-aa23-db5c20501154-image.png',
      map: null,
      mapInitialized: false,
      markers: [],
      infoWindow: null,
      CustomMarkerClass: null,
      isMobile: false,
      mapActiveOnMobile: false,
      isLoadingMap: false, // New data property for loading state
      searchQuery: '',
      filterOpenNow: false,
      selectedMusic: [],
      selectedGoodFor: []
    };
  },
  watch: {
    filteredClubs() {
      if (this.mapInitialized) {
        this.updateMarkers();
      }
    }
  },
  computed: {
    activeClubsList() {
      return Object.values(this.clubsConfig).filter(club => !club.closed && club.directions && club.directions.googleMap);
    },
    filterOptions() {
      const music = new Set();
      const goodFor = new Set();
      this.activeClubsList.forEach(club => {
        if (club.tags) {
          const musicTag = club.tags.find(t => t.label === 'MUSIC');
          if (musicTag && musicTag.value) {
            musicTag.value.split('/').forEach(m => music.add(m.trim().toUpperCase()));
          }
          const goodForTag = club.tags.find(t => t.label === 'GOOD FOR');
          if (goodForTag && goodForTag.value) goodFor.add(goodForTag.value.trim().toUpperCase());
        }
      });
      return {
        music: Array.from(music).sort(),
        goodFor: Array.from(goodFor).sort()
      };
    },
    filteredClubs() {
      const now = new Date();

      return this.activeClubsList.filter(club => {
        if (this.filterOpenNow && !this.isClubOpen(club, now)) {
          return false;
        }

        if (this.selectedMusic.length > 0) {
          const clubMusic = this.getTagValue(club.tags, 'MUSIC').toUpperCase();
          const hasMatch = this.selectedMusic.some(musicFilter => clubMusic.includes(musicFilter));
          if (!hasMatch) return false;
        }

        if (this.selectedGoodFor.length > 0) {
          const clubGoodFor = this.getTagValue(club.tags, 'GOOD FOR').toUpperCase();
          const hasMatch = this.selectedGoodFor.some(goodForFilter => clubGoodFor.includes(goodForFilter));
          if (!hasMatch) return false;
        }

        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase();
          const inName = club.name.toLowerCase().includes(query);
          const inCaption = club.caption && club.caption.toLowerCase().includes(query);
          const inTags = club.tags && club.tags.some(tag =>
            (tag.value && tag.value.toLowerCase().includes(query)) ||
            (tag.desc && tag.desc.toLowerCase().includes(query))
          );
          if (!(inName || inCaption || inTags)) return false;
        }

        return true;
      });
    },
    activeFiltersSummary() {
      const summary = [];
      if (this.filterOpenNow) summary.push('Open Now');
      if (this.selectedMusic.length > 0) summary.push(`Music: ${this.selectedMusic.join(', ')}`);
      if (this.selectedGoodFor.length > 0) summary.push(`Good For: ${this.selectedGoodFor.join(', ')}`);
      if (this.searchQuery) summary.push(`Search: "${this.searchQuery}"`);
      return summary.length > 0 ? summary.join(' | ') : '';
    }
  },
  created() {
    this.checkIfMobile();
    window.addEventListener('resize', this.checkIfMobile);
    window.history.scrollRestoration = 'manual';
  },
  mounted() {
    if (!this.isMobile) {
      this.loadGoogleMapsAPI();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkIfMobile);
  },
  methods: {
    checkIfMobile() {
      this.isMobile = window.innerWidth <= 900;
    },
    activateMap() {
      if (!this.mapInitialized && !this.isLoadingMap) {
        this.mapActiveOnMobile = true;
        this.isLoadingMap = true; // Start loading state
        this.loadGoogleMapsAPI();
      }
    },
    loadGoogleMapsAPI() {
      if (this.mapInitialized) return;

      const apiKey = 'AIzaSyCzWl07YrBfr0oF6RBikA9Wtmw3K2DKERc'; // Replace with your actual API key
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initMap`;
      script.async = true;
      script.defer = true;
      window.initMap = this.initializeMap;
      document.head.appendChild(script);
    },
    initializeMap() {
      this.mapInitialized = true;
      this.isLoadingMap = false; // End loading state

      this.CustomMarkerClass = class CustomMarker extends google.maps.OverlayView {
        constructor(position, map, clubData, onClick) {
          super();
          this.position = position;
          this.clubData = clubData;
          this.onClick = onClick;

          this.container = document.createElement('div');
          this.container.classList.add('custom-marker');
          this.container.addEventListener('click', () => this.onClick(this));

          const img = document.createElement('img');
          img.src = this.clubData.logo;
          img.alt = `${this.clubData.name} logo`;
          img.style.width = '40px';
          img.style.height = '40px';
          img.style.borderRadius = '50%';
          img.style.border = '2px solid white';
          img.style.boxShadow = '0 2px 5px rgba(0,0,0,0.4)';
          this.container.appendChild(img);

          this.setMap(map);
        }
        onAdd() { this.getPanes().overlayMouseTarget.appendChild(this.container); }
        draw() {
          const overlayProjection = this.getProjection();
          const sw = overlayProjection.fromLatLngToDivPixel(this.position);
          this.container.style.left = (sw.x - 20) + 'px';
          this.container.style.top = (sw.y - 20) + 'px';
        }
        onRemove() { if (this.container.parentElement) { this.container.parentElement.removeChild(this.container); } }
        setVisible(isVisible) { this.container.style.display = isVisible ? '' : 'none'; }
        setOpenStatus(isOpen) {
          this.container.classList.toggle('is-open', isOpen);
          this.container.classList.toggle('is-closed', !isOpen);
        }
        getPosition() { return this.position; }
      }

      const tulumCoords = { lat: 20.187, lng: -87.464 };
      this.map = new google.maps.Map(this.$refs.mapContainer, {
        center: tulumCoords,
        zoom: 12,
        mapId: 'TULUM_NIGHTLIFE_MAP_STYLE',
        styles: [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#263c3f" }] },
          { featureType: "poi.park", elementType: "labels.text.fill", stylers: [{ color: "#6b9a76" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
          { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#212a37" }] },
          { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#746855" }] },
          { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#1f2835" }] },
          { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#f3d19c" }] },
          { featureType: "transit", elementType: "geometry", stylers: [{ color: "#2f3948" }] },
          { featureType: "transit.station", elementType: "labels.text.fill", stylers: [{ color: "#d59563" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] },
          { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#515c6d" }] },
          { featureType: "water", elementType: "labels.text.stroke", stylers: [{ color: "#17263c" }] }
        ]
      });
      this.infoWindow = new google.maps.InfoWindow({
        content: '',
        pixelOffset: new google.maps.Size(0, -40) // Offset to appear above marker
      });
      this.plotAllMarkers();
      this.updateMarkers();
    },
    plotAllMarkers() {
      const now = new Date();
      this.activeClubsList.forEach(club => {
        const coords = this.extractCoordinates(club);
        if (coords) {
          const isOpen = this.isClubOpen(club, now);
          const marker = new this.CustomMarkerClass(
            new google.maps.LatLng(coords.latitude, coords.longitude),
            this.map,
            club,
            (markerInstance) => {
              const content = this.generateInfoWindowContent(markerInstance.clubData);
              this.infoWindow.setContent(content);
              this.infoWindow.open(this.map, markerInstance);
            },
          );
          marker.setOpenStatus(isOpen); // Set initial open/closed status
          this.markers.push(marker);
        }
      });
    },
    updateMarkers() {
      const filteredClubSlugs = new Set(this.filteredClubs.map(c => c.slug));
      const now = new Date();
      this.markers.forEach(marker => {
        const isVisible = filteredClubSlugs.has(marker.clubData.slug);
        marker.setVisible(isVisible);
        if (isVisible) { // Only update status for visible markers
          const isOpen = this.isClubOpen(marker.clubData, now);
          marker.setOpenStatus(isOpen);
        }
      });
      if (this.infoWindow) {
        this.infoWindow.close();
      }
    },
    generateInfoWindowContent(club) {
      const clubImage = club.main_image?.imgix_url || club.logo || 'https://placehold.co/100x100/333/fff?text=No+Image';
      const musicTags = this.getTagValue(club.tags, 'MUSIC');
      const goodForTags = this.getTagValue(club.tags, 'GOOD FOR');
      const description = club.caption || club.short_description || 'Discover more about this vibrant Tulum spot!';

      return `
            <div style="font-family: 'Inter', sans-serif; max-width: 280px; background-color: #2b2b2d; color: #f0f0f0; padding: 15px; border-radius: 8px; box-shadow: 0 4px 15px rgba(0,0,0,0.3);">
                <div style="display: flex; align-items: center; margin-bottom: 10px;">
                    <img src="${clubImage}" alt="${club.name}" style="width: 70px; height: 70px; object-fit: cover; border-radius: 8px; margin-right: 15px; border: 2px solid #555;">
                    <div>
                        <h3 style="margin: 0; font-size: 1.3em; color: #fff; font-weight: bold;">${club.name}</h3>
                        <p style="margin: 2px 0 0 0; font-size: 0.85em; color: #ccc;">${description}</p>
                    </div>
                </div>
                <p style="margin: 10px 0; font-size: 0.9em; color: #ddd;">
                    <strong>Music:</strong> ${musicTags || 'N/A'}<br>
                    <strong>Good For:</strong> ${goodForTags || 'N/A'}
                </p>
                <div style="display: flex; justify-content: space-between; align-items: center; padding-top: 10px; border-top: 1px solid #444;">
                    <span style="font-weight: bold; color: #d4af37; font-size: 1.2em;">${this.formatPriceRange(club.price)}</span>
                    <a href="${club.slug}" target="_blank" style="text-decoration: none; background-color: #73854a; color: white; padding: 8px 15px; border-radius: 6px; font-size: 0.95em; font-weight: bold; transition: background-color 0.2s ease;">View Details</a>
                </div>
            </div>
        `;
    },
    clearFilters() { this.searchQuery = ''; this.filterOpenNow = false; this.selectedMusic = []; this.selectedGoodFor = []; },
    isClubOpen(club, now) {
      if (!club.tags) return false;
      const jsDay = now.getDay();
      const ourDaySpec = jsDay === 0 ? 7 : jsDay; // Sunday is 0 in JS, 7 in our spec
      const openDays = club.daysOpen || [1, 2, 3, 4, 5, 6, 7];
      if (!openDays.includes(ourDaySpec)) {
        return false;
      }
      const openingSpecs = this.generateOpeningHours(club, true);
      const currentTime = now.getHours() * 100 + now.getMinutes();

      for (const spec of openingSpecs) {
        const opens = parseInt(spec.opens.replace(':', ''), 10);
        let closes = parseInt(spec.closes.replace(':', ''), 10);

        // Handle overnight closing (e.g., 22:00 - 04:00)
        if (closes < opens) {
          if (currentTime >= opens || currentTime < closes) {
            return true;
          }
        } else {
          if (currentTime >= opens && currentTime < closes) {
            return true;
          }
        }
      }
      return false;
    },
    getTagValue(tags, label) { if (!tags) return ''; const tag = tags.find(t => t.label === label); return tag ? tag.value : ''; },
    formatPriceRange(price) { return price >= 1 && price <= 4 ? '$'.repeat(price) : '$$$'; },
    extractCoordinates(club) { return { latitude: parseFloat(club.directions.latitude), longitude: parseFloat(club.directions.longitude) }; },
    generateOpeningHours(club, forCheck = false) {
      if (!club.tags) return [];
      const daySpecMap = { 1: "Monday", 2: "Tuesday", 3: "Wednesday", 4: "Thursday", 5: "Friday", 6: "Saturday", 7: "Sunday" };
      const openDays = club.daysOpen || [1, 2, 3, 4, 5, 6, 7];
      const schemaDays = openDays.map(dayNum => daySpecMap[dayNum]);

      const parseTime = (str) => {
        if (!str || str.includes('--') || !str.match(/(\d+)(AM|PM)?/)) return null;
        str = str.toUpperCase().trim();
        let [time, modifier] = str.match(/(\d+)(AM|PM)?/).slice(1);
        let [hours] = time.split(':');
        hours = parseInt(hours, 10);
        if (modifier === 'PM' && hours < 12) hours += 12;
        if (modifier === 'AM' && hours === 12) hours = 0;
        return `${String(hours).padStart(2, '0')}:00`;
      };

      const hours = [];
      const dayTag = club.tags.find(t => t.label === 'DAY');
      const nightTag = club.tags.find(t => t.label === 'NIGHT');

      if (dayTag && !dayTag.value.includes('--')) {
        const [opens, closes] = dayTag.value.split('-');
        const opensTime = parseTime(opens);
        const closesTime = parseTime(closes);
        if (opensTime && closesTime) {
          hours.push({ dayOfWeek: schemaDays, opens: opensTime, closes: closesTime });
        }
      }
      if (nightTag && !nightTag.value.includes('--')) {
        const [opens, closesRaw] = nightTag.value.split('-');
        const opensTime = parseTime(opens);
        let closesTime = parseTime(closesRaw);
        if (closesRaw.toUpperCase().includes('LATE')) closesTime = '02:00';
        else if (!closesTime) closesTime = '02:00'; // Default late closing if not specified
        if (opensTime && closesTime) {
          hours.push({ dayOfWeek: schemaDays, opens: opensTime, closes: closesTime });
        }
      }
      return forCheck ? hours : hours.map(h => ({ '@type': 'OpeningHoursSpecification', dayOfWeek: h.dayOfWeek, opens: h.opens, closes: h.closes }));
    }
  }
};
</script>

<style scoped>
.map-page-container {
  display: flex;
  height: 100vh;
  overflow: hidden;
  background-color: #1a1a1c;
  color: #f0f0f0;
}

.map-controls {
  width: 380px;
  flex-shrink: 0;
  padding: 0;
  background-color: #2b2b2d;
  overflow-y: auto;
  border-right: 1px solid #444;
  order: 1;
  position: sticky; /* Make controls sticky on desktop */
  top: 0; /* Stick to the top */
  height: 100vh; /* Ensure it takes full height to scroll its content */
}

/* ✅ NEW Styles for Lazy Map Placeholder */
.map-placeholder {
  position: relative;
  cursor: pointer;
  order: 1;
  width: 100%;
  height: 100%; /* Take full height on mobile */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.map-placeholder .responsive-image-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(0.6); /* Darken image for better text contrast */
}

.placeholder-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5em;
  font-weight: bold;
  text-align: center;
  transition: background-color 0.3s ease;
  z-index: 10; /* Ensure overlay is above image */
}

.map-placeholder:hover .placeholder-overlay {
  background-color: rgba(0, 0, 0, 0.7);
}

/* ✅ NEW Styles for Loading Spinner */
.loading-spinner-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7); /* Darker overlay during loading */
  color: white;
  z-index: 20; /* Above placeholder overlay */
  position: absolute; /* Position over map container */
  top: 0;
  left: 0;
}

.spinner {
  border: 8px solid rgba(255, 255, 255, 0.3);
  border-top: 8px solid #73854a; /* Green spinner */
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

#google-map-container {
  width: 100%;
  height: 100%;
  order: 2;
  position: relative; /* Needed for absolute positioning of spinner */
}

/* ✅ NEW Styles for Custom HTML Markers */
.custom-marker {
  position: absolute;
  background-color: #1a1818;
  border-radius: 50%;
  transition: transform 0.2s ease-out, border-color 0.3s ease, opacity 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0,0,0,0.4);
}

.custom-marker img {
  display: block;
}

.custom-marker.is-open {
  border: 3px solid #4CAF50; /* Green border for open */
  opacity: 1;
  transform: scale(1.1); /* Slightly larger when open */
}

.custom-marker.is-closed {
  border: 3px solid #FF4081; /* Red border for closed */
  opacity: 0.7;
  filter: grayscale(50%); /* Slightly desaturated when closed */
}

.custom-marker:hover {
  transform: scale(1.2); /* Larger on hover */
  z-index: 100; /* Bring to front on hover */
}

/* ✅ NEW Styles for Active Filters Summary */
.active-filters-summary {
  background-color: #3a3a3a;
  padding: 10px 15px;
  border-radius: 8px;
  margin: 15px 20px;
  font-size: 0.85em;
  color: #ccc;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.2);
}

@media (max-width: 900px) {
  .map-page-container {
    flex-direction: column;
    height: auto; /* Allow content to define height */
  }
  .map-controls {
    width: 100%;
    order: 2;
    border-right: none;
    height: auto; /* Allow controls to expand with content */
    position: static; /* Remove sticky on mobile */
  }
  #google-map-container {
    height: 60vh; /* Fixed height for map on mobile */
    order: 1;
  }
  .map-placeholder {
    height: 60vh; /* Fixed height for placeholder on mobile */
  }
  .map-controls .responsive-image-container {
    display: none; /* Hide header image within controls on mobile */
  }
}
</style>
